import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

export const PermissionMatrix = () => {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState("org_owner");
  const canEdit = auth.can("users", "edit");

  const load = useCallback(() => api.get("/platform/permissions").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, auth.user?.tenant_id]);

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const row = data.matrix[role] || {};
  const locked = !canEdit || role === "platform_admin";

  const toggle = async (module, action, value) => {
    try {
      await api.put("/platform/permissions", { role, module, actions: { ...row[module], [action]: value } });
      await load();
      auth.refreshMe();
    } catch (e) {
      toast.error(apiError(e, "Could not update permission"));
    }
  };

  const reset = async () => {
    await api.post("/platform/permissions/reset", { role });
    toast.success("Role reset to defaults");
    load();
    auth.refreshMe();
  };

  const groups = [...new Set(data.modules.map((m) => m.group))];

  return (
    <div className="space-y-4" data-testid="permission-matrix">
      <div className="flex flex-wrap gap-1.5">
        {data.roles.map((r) => (
          <button key={r.key} onClick={() => setRole(r.key)} data-testid={`perm-role-${r.key}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    role === r.key ? "bg-[#003366] text-white border-[#003366]" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          {role === "platform_admin" ? "Platform Admin always has full access." :
            `Legacy access level: ${data.roles.find((r) => r.key === role)?.legacy}. Unticked actions are blocked across the app.`}
        </span>
        {canEdit && role !== "platform_admin" && (
          <Button size="sm" variant="ghost" onClick={reset} data-testid="perm-reset-role"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset role to defaults</Button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-4 py-3 text-left">Module</th>
              {data.actions.map((a) => <th key={a} className="px-3 py-3 text-center">{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g}>
                <tr><td colSpan={7} className="px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{g}</td></tr>
                {data.modules.filter((m) => m.group === g).map((m) => {
                  const overridden = data.overrides.includes(`${role}:${m.key}`);
                  return (
                    <tr key={m.key} className="border-b border-slate-50" data-testid={`perm-row-${m.key}`}>
                      <td className="px-4 py-2.5 text-[13px] text-[#003366]">
                        {m.label}{overridden && <span className="ml-2 text-[10px] font-semibold text-amber-700">customised</span>}
                      </td>
                      {data.actions.map((a) => {
                        const cellLocked = (data.locked?.[m.key]?.[a] || []).includes(role);
                        return (
                          <td key={a} className="px-3 py-2.5 text-center">
                            <span title={cellLocked ? data.locked_reason : undefined} className="inline-flex" data-testid={`perm-${m.key}-${a}-wrap`}>
                              <Checkbox checked={!!row[m.key]?.[a]} disabled={locked || cellLocked}
                                        onCheckedChange={(v) => toggle(m.key, a, !!v)} data-testid={`perm-${m.key}-${a}`} />
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
